/**
 * Summary tab printing — Summary (the pivot as shown) and Detail (every booking
 * behind each service). Both build a real landscape PDF (jsPDF + autotable) and
 * download directly (no popup), matching the Bookings / menu-card pattern. The
 * saved per-service note prints with each service.
 */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import type { Service, ServiceNote } from "./MasterView";
import type { PdfOrientation } from "./orientation";

export interface DetailPrintRow {
  JourneyDate: string | null;
  JourneyTime: string | null;
  Day: string | null;
  JourneyCode: string | null;
  Carriage: string | null;
  Seats: string | null;
  Booking: number | null;
  AccName: string | null;
  Item: string | null;
  Courses: number | null;
  Covers: number | null;
  Price: number | null;
  Notes: string | null;
}

type PdfCell = string | { content: string; colSpan?: number; styles?: Record<string, unknown> };
type PdfBody = PdfCell[][];
type AutoTableInput = import("jspdf-autotable").RowInput[];

const num = new Intl.NumberFormat("en-GB");
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function prettyDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}
function carriageLabel(c: string): string {
  return c.replace(/^Pullman\s*-\s*/i, "").trim() || c;
}
function money(v: unknown): string {
  if (v == null || v === "") return "";
  const n = Number(v);
  return Number.isFinite(n) ? `£${n.toFixed(2)}` : "";
}
function lastY(doc: jsPDF, fallback: number): number {
  return (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? fallback;
}
function svcKeyOf(journeyDate: string, journeyTime: string): string {
  return `${journeyDate}|${journeyTime.slice(11)}`;
}
function headerBand(doc: jsPDF, title: string, range: { from: string; to: string }, countingLabel?: string) {
  const printedOn = new Date().toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
  });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(11, 61, 46);
  doc.text(title, 8, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(90, 90, 90);
  doc.text(`${range.from} to ${range.to}${countingLabel ? `  ·  ${countingLabel}` : ""}  ·  printed ${printedOn}`, 8, 17);
}

export function printSummaryPdf(
  services: Service[],
  occ: Map<string, number>,
  notes: Map<string, ServiceNote>,
  range: { from: string; to: string },
  orientation: PdfOrientation = "landscape",
  countingLabel?: string
) {
  const doc = new jsPDF({ orientation, unit: "mm", format: "a4" });
  const pageH = orientation === "landscape" ? 210 : 297;
  const usableW = (orientation === "landscape" ? 297 : 210) - 16; // 8mm margins each side
  const yBreak = pageH - 30;
  headerBand(doc, "Dining & Events — Summary", range, countingLabel);
  const cell = (n: number) => (n ? num.format(n) : "");
  let y = 24;

  for (const svc of services) {
    if (y > yBreak) { doc.addPage(); y = 15; }
    const cars = svc.carriages;
    const grp = (pred: (it: Service["items"][number]) => boolean) => {
      const byCar: Record<string, number> = {};
      let total = 0;
      for (const it of svc.items) {
        if (!pred(it)) continue;
        for (const c of cars) byCar[c] = (byCar[c] ?? 0) + (it.byCarriage[c] ?? 0);
        total += it.total;
      }
      return { byCar, total };
    };
    const seats = grp((it) => it.seq === 0 && !/Exclusivity/i.test(it.item));
    const excl = grp((it) => it.seq === 0 && /Exclusivity/i.test(it.item));
    const starters = grp((it) => it.seq === 1);
    const mains = grp((it) => it.seq === 2);
    const desserts = grp((it) => it.seq === 3);

    const skey = svcKeyOf(svc.journeyDate, svc.journeyTime);
    const seatsSold = occ.get(skey);
    const time = svc.journeyTime.slice(11) || svc.journeyTime;
    const title = `${svc.day} ${prettyDate(svc.journeyDate)} · ${time}${svc.journey ? "  —  " + svc.journey : ""}`;

    let recon = `Food covers ${num.format(seats.total)}`;
    if (excl.total) recon += ` · exclusivity ${num.format(excl.total)} (no meal)`;
    if (seatsSold != null) {
      const noMeal = seatsSold - seats.total - excl.total;
      recon = `Seats sold (Pullman) ${num.format(seatsSold)} = food covers ${num.format(seats.total)}`
        + (excl.total ? ` + exclusivity ${num.format(excl.total)}` : "")
        + (noMeal ? ` + no-meal/unmatched ${num.format(noMeal)}` : "");
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.setTextColor(11, 61, 46);
    doc.text(title, 8, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(90, 90, 90);
    doc.text(recon, 8, y + 4.2);

    const head = [["Item", ...cars.map(carriageLabel), "Items Total"]];
    const body: PdfBody = svc.items.map((it) => [
      it.item,
      ...cars.map((c) => cell(it.byCarriage[c] ?? 0)),
      cell(it.total),
    ]);
    const reconRow = (label: string, g: { byCar: Record<string, number>; total: number }): PdfCell[] => [
      { content: label, styles: { fontStyle: "bold" } },
      ...cars.map((c) => ({ content: cell(g.byCar[c] ?? 0), styles: { fontStyle: "bold", halign: "right" } })),
      { content: cell(g.total), styles: { fontStyle: "bold", halign: "right", fillColor: [240, 244, 241] } },
    ];
    body.push(reconRow("Seats (food)", seats));
    if (excl.total) body.push(reconRow("Exclusivity seats", excl));
    body.push(reconRow("Starters total", starters));
    body.push(reconRow("Mains total", mains));
    body.push(reconRow("Desserts total", desserts));

    const columnStyles: Record<number, Record<string, unknown>> = { 0: { cellWidth: 58, halign: "left" } };
    for (let i = 1; i <= cars.length + 1; i++) columnStyles[i] = { halign: "right" };

    autoTable(doc, {
      head,
      body: body as unknown as AutoTableInput,
      startY: y + 7,
      margin: { left: 8, right: 8 },
      styles: { fontSize: 7.5, cellPadding: 1.3, overflow: "linebreak", valign: "top" },
      headStyles: { fillColor: [31, 77, 63], textColor: 255, fontStyle: "bold" },
      columnStyles,
    });
    y = lastY(doc, y + 7) + 5;

    const note = notes.get(skey)?.note;
    if (note && note.trim()) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(11, 61, 46);
      doc.text("Service notes", 8, y);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(40, 40, 40);
      const lines = doc.splitTextToSize(note.trim(), usableW);
      doc.text(lines, 8, y + 4);
      y += 4 + lines.length * 3.6 + 4;
    }
    y += 6;
  }

  doc.save(`summary-${range.from}_to_${range.to}.pdf`);
}

export function printDetailPdf(
  rows: DetailPrintRow[],
  notes: Map<string, ServiceNote>,
  range: { from: string; to: string },
  orientation: PdfOrientation = "landscape",
  countingLabel?: string
) {
  const doc = new jsPDF({ orientation, unit: "mm", format: "a4" });
  const pageH = orientation === "landscape" ? 210 : 297;
  const usableW = (orientation === "landscape" ? 297 : 210) - 16; // 8mm margins each side
  const yBreak = pageH - 32;
  headerBand(doc, "Dining & Events — Summary detail (bookings per service)", range, countingLabel);

  // Group service -> booking, preserving the incoming (sorted) order.
  interface Bk { header: string; items: DetailPrintRow[] }
  interface Svc { title: string; skey: string; bookings: Bk[]; bkIndex: Map<string, Bk> }
  const svcs: Svc[] = [];
  const svcIndex = new Map<string, Svc>();
  for (const r of rows) {
    const jd = r.JourneyDate ?? "";
    const jt = r.JourneyTime ?? "";
    const skey = svcKeyOf(jd, jt);
    let svc = svcIndex.get(skey);
    if (!svc) {
      const time = jt.slice(11) || jt;
      const title = `${r.Day ?? ""} ${prettyDate(jd)} · ${time}${r.JourneyCode ? "  —  " + r.JourneyCode : ""}`.trim();
      svc = { title, skey, bookings: [], bkIndex: new Map() };
      svcIndex.set(skey, svc);
      svcs.push(svc);
    }
    const bkNo = r.Booking == null ? "—" : String(r.Booking);
    let bk = svc.bkIndex.get(bkNo);
    if (!bk) {
      const acc = r.AccName || "(no account)";
      const bits = [acc, `Booking ${bkNo}`, r.Carriage || "", r.Seats ? `Seats ${r.Seats}` : ""].filter(Boolean);
      bk = { header: bits.join("   ·   "), items: [] };
      svc.bkIndex.set(bkNo, bk);
      svc.bookings.push(bk);
    }
    bk.items.push(r);
  }

  let y = 24;
  for (const svc of svcs) {
    if (y > yBreak) { doc.addPage(); y = 15; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.setTextColor(11, 61, 46);
    doc.text(svc.title, 8, y);
    y += 4;

    const note = notes.get(svc.skey)?.note;
    if (note && note.trim()) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(90, 90, 90);
      const lines = doc.splitTextToSize(`Notes: ${note.trim()}`, usableW);
      doc.text(lines, 8, y + 2);
      y += 2 + lines.length * 3.4;
    }

    const head = [["Item", "Courses", "Covers", "Price", "Notes"]];
    const body: PdfBody = [];
    for (const bk of svc.bookings) {
      body.push([
        { content: bk.header, colSpan: 5, styles: { fontStyle: "bold", fillColor: [224, 236, 230], textColor: [11, 61, 46] } },
      ]);
      for (const it of bk.items) {
        const courses = Number(it.Courses ?? 0) || 0;
        const covers = Number(it.Covers ?? 0) || 0;
        const price = Number(it.Price ?? 0) || 0;
        body.push([
          it.Item ?? "",
          courses ? String(courses) : "",
          covers ? String(covers) : "",
          price ? money(price) : "",
          it.Notes ?? "",
        ]);
      }
    }

    autoTable(doc, {
      head,
      body: body as unknown as AutoTableInput,
      startY: y + 3,
      margin: { left: 8, right: 8 },
      styles: { fontSize: 7.5, cellPadding: 1.3, overflow: "linebreak", valign: "top" },
      headStyles: { fillColor: [31, 77, 63], textColor: 255, fontStyle: "bold" },
      columnStyles: {
        0: { cellWidth: 80 },
        1: { halign: "right", cellWidth: 18 },
        2: { halign: "right", cellWidth: 18 },
        3: { halign: "right", cellWidth: 24 },
        4: { cellWidth: "auto" },
      },
    });
    y = lastY(doc, y + 3) + 8;
  }

  doc.save(`summary-detail-${range.from}_to_${range.to}.pdf`);
}
